/**
 * Wrapper function for importScene to handle file upload
 * @param {File} file - The file to import
 * @returns {Promise} - A promise that resolves when the import is complete
 */
const handleImportScene = (file) => {
  return importScene(file)
} // src/Planner.jsx
import React, { useState, useEffect, useRef, useMemo } from "react"
import logo from "./assets/logo-laCividina.png"
import { FaChevronDown, FaChevronRight, FaSpinner } from "react-icons/fa"

import placeholder from "./assets/placeholder.jpg"

// Base materials
import materiali from "./materials-stuff" // fabrics, etc.
import wood from "./collections/wood-metal"

// Local product list
import products from "./products"
import PlannerPlayer from "./PlannerPlayer"
import ProductList from "./components/ProductsList"
import PlannerActions from "./components/PlannerActions"

// 5° rotation steps (changed from 15° to 5°)
const angles = Array.from({ length: 73 }, (_, i) => i * 5)
const Y_OFFSET = 1.0

// Bilingual labels
const labels = {
  family: "Famiglia / Family",
  selectModel: "Seleziona modello / Select model",
  allProducts: "TUTTI I PRODOTTI / ALL PRODUCTS",
  floor: {
    length: "L",
    width: "W",
    height: "H",
  },
  addedProducts: "Prodotti aggiunti / Added products",
  noProducts: "Nessun prodotto aggiunto / No products added yet",
  materials: "Materiali / Materials",
  upholstery: "Rivestimento / Upholstery",
  structure: "Struttura / Structure",
  finish: "Finitura / Finish",
  searchPlaceholder: "Cerca... / Search...",
  allCategories: "TUTTE LE CATEGORIE / ALL CATEGORIES",
  noConfigurableMaterials:
    "Nessun materiale configurabile / No configurable materials",
  noMaterialsAvailable: "Nessun materiale disponibile / No materials available",
  designSpace: "PROGETTA SPAZIO / DESIGN SPACE", // New label for SET
}

// Local storage keys
const LS_KEYS = {
  CATEGORIES: "lacividina_categories",
  PRODUCTS_LIST: "lacividina_products_list",
  FAMILIES: "lacividina_families",
  LAST_FETCH: "lacividina_last_fetch",
}

// Cache timeout in milliseconds (24 hours)
const CACHE_TIMEOUT = 24 * 60 * 60 * 1000

/**
 * Main Planner logic
 */
const Planner = () => {
  const iframeRef = useRef(null)
  const emptyImageRef = useRef(null)
  const nodeLocalIdsRef = useRef(1)

  const [loading, setLoading] = useState(true)
  const [loadingProducts, setLoadingProducts] = useState({})

  // "Projects" data from viewer
  const [categories, setCategories] = useState([])
  const [productsList, setProductsList] = useState([]) // productNodes from getPlannerUsableProjects
  const [families, setFamilies] = useState([])
  const [selectedFamily, setSelectedFamily] = useState(null)

  // State of ProductNode instances in scene
  const [productNodes, setProductNodes] = useState({})
  const [productNodesToAdd, setProductNodesToAdd] = useState({})

  const [ignoreClick, setIgnoreClick] = useState(false)

  // UI states and toggles
  const [isAccordionHidden, setIsAccordionHidden] = useState(false)
  const [floorDimensions, setFloorDimensions] = useState({
    length: 1200.0,
    width: 1200.0,
    height: 0.1,
  })

  // "Select Model" menu management
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false)
  const productDropdownRef = useRef(null)
  const [isFamilyDropdownOpen, setIsFamilyDropdownOpen] = useState(false)
  const familyDropdownRef = useRef(null)

  // For managing the expansion/closing of material accordions
  const [accordionOpenState, setAccordionOpenState] = useState({})

  // To track the Emersya instance (fix for emViewers access)
  const [emersyaViewerInstance, setEmersyaViewerInstance] = useState(null)

  // Organize families in alphabetical order with PAVIMENTI and SET at the end
  const sortedFamilies = useMemo(() => {
    if (!families.length) return []

    const regularFamilies = families
      .filter((f) => f !== "PAVIMENTO" && f !== "SET")
      .sort((a, b) => a.localeCompare(b))

    // Force specific order: PAVIMENTO first, then SET
    const specialFamilies = []
    if (families.includes("PAVIMENTO")) specialFamilies.push("PAVIMENTO")
    if (families.includes("SET")) specialFamilies.push("SET")

    return [...regularFamilies, ...specialFamilies]
  }, [families])

  // Display name mapping function
  const getFamilyDisplayName = (family) => {
    if (family === "PAVIMENTO") return "INSERISCI PAVIMENTO / INSERT FLOOR"
    if (family === "SET") return labels.designSpace // Updated to use the new label
    return family.toUpperCase()
  }

  /**
   * Resets the scene by removing all product nodes
   */
  const resetScene = () => {
    // Remove all product nodes from the Emersya viewer
    Object.keys(productNodes).forEach((localId) => {
      iframeRef.current.contentWindow.postMessage(
        {
          action: "removeProductNodeInstance",
          localId: parseInt(localId),
          id: parseInt(localId),
        },
        "*"
      )
    })

    // Clear the state
    setProductNodes({})
    setProductNodesToAdd({})
    setLoadingProducts({})
  }

  /* ----------------------------------------------------------------
    1) Setup: listeners for clicks outside dropdown, empty image, etc.
  -----------------------------------------------------------------*/
  // Close dropdowns if clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target)
      ) {
        setIsProductDropdownOpen(false)
      }

      if (
        familyDropdownRef.current &&
        !familyDropdownRef.current.contains(event.target)
      ) {
        setIsFamilyDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Transparent image (ghost) for drag
  useEffect(() => {
    const emptyImage = new Image()
    emptyImage.src =
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAB3RJTUUH4QYHDTQ15hoLGQAAAB1pVFh0Q29tbWVudAAAAAAAQ3JlYXRlZCB3aXRoIEdJTVBkLmUHAAAAC0lEQVQI12NgAAIAAAUAAeImBZsAAAAASUVORK5CYII="
    emptyImageRef.current = emptyImage
  }, [])

  // Check cache on mount and load data if available
  useEffect(() => {
    loadCachedData()
  }, [])

  // Load data from cache if available
  const loadCachedData = () => {
    try {
      const lastFetch = localStorage.getItem(LS_KEYS.LAST_FETCH)
      const now = new Date().getTime()

      // Check if cache is still valid (within 24 hours)
      if (lastFetch && now - parseInt(lastFetch) < CACHE_TIMEOUT) {
        const cachedCategories = JSON.parse(
          localStorage.getItem(LS_KEYS.CATEGORIES)
        )
        const cachedProductsList = JSON.parse(
          localStorage.getItem(LS_KEYS.PRODUCTS_LIST)
        )
        const cachedFamilies = JSON.parse(
          localStorage.getItem(LS_KEYS.FAMILIES)
        )

        if (cachedCategories && cachedProductsList && cachedFamilies) {
          console.log("[CACHE] Loading data from cache")
          setCategories(cachedCategories)
          setProductsList(cachedProductsList)
          setFamilies(cachedFamilies)
          resizeFloor()
          setLoading(false)
          return true
        }
      }
    } catch (error) {
      console.error("[CACHE ERROR]", error)
      // Clear any corrupted cache
      clearCache()
    }
    return false
  }

  // Save data to cache
  const saveToCache = (categoriesData, productsListData, familiesData) => {
    try {
      localStorage.setItem(LS_KEYS.CATEGORIES, JSON.stringify(categoriesData))
      localStorage.setItem(
        LS_KEYS.PRODUCTS_LIST,
        JSON.stringify(productsListData)
      )
      localStorage.setItem(LS_KEYS.FAMILIES, JSON.stringify(familiesData))
      localStorage.setItem(LS_KEYS.LAST_FETCH, new Date().getTime().toString())
      console.log("[CACHE] Data saved to cache")
    } catch (error) {
      console.error("[CACHE ERROR]", error)
    }
  }

  // Clear cache
  const clearCache = () => {
    try {
      localStorage.removeItem(LS_KEYS.CATEGORIES)
      localStorage.removeItem(LS_KEYS.PRODUCTS_LIST)
      localStorage.removeItem(LS_KEYS.FAMILIES)
      localStorage.removeItem(LS_KEYS.LAST_FETCH)
    } catch (error) {
      console.error("[CACHE ERROR]", error)
    }
  }

  // Iframe message listener
  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data

      if (!data) return

      switch (data.action) {
        case "onError":
          handleError(data)
          break
        case "onSuccess":
          handleSuccess(data)
          break
        case "onProductNodeInstanceConfigurableMaterialsGet":
          handleConfigurableMaterials(data)
          break
        case "onCollisionsUpdate":
          handleCollisionsUpdate(data)
          break
        case "onMeshSelection":
          handleMeshSelection(data)
          break
        case "onStateChange":
          handleStateChange(data)
          break
        case "onProductNodeInstanceDraggingUpdate":
          if (productNodes[data.nodeLocalId]) {
            const pn = productNodes[data.nodeLocalId]
            console.log(pn)
            pn.position = data.position
          }
          if (productNodesToAdd[data.nodeLocalId]) {
            const pn = productNodesToAdd[data.nodeLocalId]
            pn.position = data.position
          }
          break

        case "onProductNodeInstanceDraggingStopped":
          // The stop event always follows an update event
          // so we don't need to update position here
          break
        default:
          // console.log("Unknown action:", data);
          break
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [productNodes, productNodesToAdd])

  // Listener for Emersya initialization
  useEffect(() => {
    const handleEmersyaInit = () => {
      if (window.emViewers && window.emViewers["emersyaIframe"]) {
        console.log("Emersya viewer initialized and available")
        setEmersyaViewerInstance(window.emViewers["emersyaIframe"])
      }
    }

    // Check if already initialized
    if (window.emViewers && window.emViewers["emersyaIframe"]) {
      handleEmersyaInit()
    }

    // Listen for initialization
    document.addEventListener("emersyaViewerInitialized", handleEmersyaInit)

    return () => {
      document.removeEventListener(
        "emersyaViewerInitialized",
        handleEmersyaInit
      )
    }
  }, [])

  // On iframe load, register as callback
  const handleIframeLoad = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage(
        { action: "registerCallback" },
        "*"
      )
    }
  }

  /* ----------------------------------------------------------------
     2) HANDLER for errors and successes from viewer
  -----------------------------------------------------------------*/
  const handleError = (data) => {
    const { callAction, id, message } = data
    console.error(`[EMERSYA ERROR] in ${callAction}: ${message}`)

    // If adding the productNode fails, remove it from the "toAdd" queue
    if (
      callAction === "startProductNodeDrop" ||
      callAction === "addProductNodeInstance" ||
      callAction === "addProductNodesInstances"
    ) {
      setProductNodesToAdd((prev) => {
        const updated = { ...prev }
        delete updated[id]
        return updated
      })

      // Remove loading state
      setLoadingProducts((prev) => {
        const updated = { ...prev }
        delete updated[id]
        return updated
      })
    }
  }

  const [openGroups, setOpenGroups] = useState({})

  const duplicateProductNode = (pn) => (event) => {
    event.stopPropagation()
    event.preventDefault()

    const localId = nodeLocalIdsRef.current + 1
    nodeLocalIdsRef.current += 1

    // Get count of existing products with same projectCode for offset calculation
    const existingCount = Object.values(productNodes).filter(
      (p) => p.projectCode === pn.projectCode
    ).length

    // Create spiral-like placement for duplicates
    const angle = (existingCount * Math.PI) / 4 // 45-degree increments
    const radius = 0.5 // Base radius
    const offsetPosition = [...pn.position]
    offsetPosition[0] += Math.cos(angle) * radius // X offset
    offsetPosition[2] += Math.sin(angle) * radius // Z offset

    // Set loading state
    setLoadingProducts((prev) => ({
      ...prev,
      [localId]: true,
    }))

    const nodes = [
      {
        SKU: pn.SKU,
        projectCode: pn.projectCode,
        localId,
        parentLocalId: 1,
        draggable: true,
        tracked: true,
        position: offsetPosition,
        rotationAxis: [0, 1, 0],
        rotationAngle: pn.rotationAngle || 0,
        configurableMaterials: [],
      },
    ]

    iframeRef.current.contentWindow.postMessage(
      {
        action: "addProductNodesInstances",
        id: localId,
        nodes,
      },
      "*"
    )

    setProductNodesToAdd((prev) => ({
      ...prev,
      [localId]: {
        SKU: pn.SKU,
        projectCode: pn.projectCode,
        localId,
        parentLocalId: 1,
        refData: pn.refData,
        position: offsetPosition,
        rotationAxis: [0, 1, 0],
        rotationAngle: pn.rotationAngle || 0,
        height: pn.height || 1,
      },
    }))
  }

  const handleSuccess = (data) => {
    const { callAction, id, data: responseData } = data
    switch (callAction) {
      case "startProductNodeDrop":
      case "addProductNodeInstance":
      case "addProductNodesInstances":
        if (productNodesToAdd[id]) {
          const pn = productNodesToAdd[id]
          console.log(
            "[SUCCESS] Added productNode with localId =",
            pn.localId,
            " => projectCode =",
            pn.projectCode
          )
          console.log(data)
          // Add to scene
          setProductNodes((prev) => ({
            ...prev,
            [pn.localId]: {
              ...pn,
              position: [pn.position[0], pn.position[1] + 0.01, pn.position[2]],
            },
          }))

          // Remove from toAdd queue
          setProductNodesToAdd((prev) => {
            const updated = { ...prev }
            delete updated[id]
            return updated
          })

          // Remove loading state
          setLoadingProducts((prev) => {
            const updated = { ...prev }
            delete updated[id]
            return updated
          })

          // Request configurable materials
          iframeRef.current.contentWindow.postMessage(
            {
              action: "getProductNodeInstanceConfigurableMaterials",
              localId: pn.localId,
            },
            "*"
          )

          // Do direct matching using projectCode
          matchProductWithLocal(pn.localId, pn.projectCode)
        }
        break
      case "getPlannerUsableProjects":
        // Check if we already have data in cache and state
        if (
          categories.length > 0 &&
          productsList.length > 0 &&
          families.length > 0
        ) {
          console.log("[CACHE] Using cached data instead of API response")
          break
        }

        setCategories(responseData)
        if (Array.isArray(responseData)) {
          // Extract all productNodes
          const allProducts = []
          const familiesSet = new Set()
          responseData.forEach((category) => {
            category.projects.forEach((project) => {
              project.productNodes.forEach((productNode) => {
                const productFamily = project.familyName || category.name || ""
                allProducts.push({
                  ...productNode,
                  projectCode: project.code,
                  family: productFamily,
                  category: category.name,
                })
                familiesSet.add(productFamily)
              })
            })
          })

          setProductsList(allProducts)
          setFamilies(Array.from(familiesSet))

          console.log("[getPlannerUsableProjects] TOT =", allProducts.length)
          console.log(allProducts) // Useful log

          // Cache the data
          saveToCache(responseData, allProducts, Array.from(familiesSet))
        }
        resizeFloor()
        setLoading(false)
        break

      case "removeProductNodeInstance":
        setProductNodes((prev) => {
          const updated = { ...prev }
          delete updated[data.id]
          return updated
        })
        break

      case "resizePlannerSpace":
        // No specific action
        break

      case "setMaterials":
        console.log("[SUCCESS] Material applied:", responseData)
        break

      default:
        console.log("[EMERSYA] Unhandled success action:", callAction)
    }
  }

  // Function to fetch similar to what you did in FamilyAccordion
  // to retrieve the p/XXXXX/presets string from HTML and extract XXXXX
  const matchProductWithLocal = (localId, projectCodeFromViewer) => {
    console.log(
      `[matchProductWithLocal] localId=${localId}, projectCodeFromViewer=${projectCodeFromViewer}`
    )

    // Try to find a matching product in products.js using projectCode
    const matched = products.find(
      (p) => p.projectCode === projectCodeFromViewer
    )

    // Update productNodes state with the matched product (or null if no match)
    setProductNodes((prev) => {
      const prevPN = prev[localId]
      if (!prevPN) return prev

      // Make sure configurableMaterials is always an array
      const configurableMaterials = prevPN.configurableMaterials || []

      return {
        ...prev,
        [localId]: {
          ...prevPN,
          matchedLocalProduct: matched || null,
          // Make sure refData contains the previewPath
          refData: {
            ...prevPN.refData,
            previewPath:
              prevPN.refData?.previewPath ||
              `https://cdn.emersya.com/cl/3IB82Q24UW/p/${projectCodeFromViewer}/productNodes/86003`,
            name: matched ? matched.name : prevPN.refData?.name || "Prodotto",
          },
          configurableMaterials,
        },
      }
    })

    console.log(
      matched
        ? `[MATCH] Found match with products.js => ${matched.name}`
        : `[NO MATCH] No match in products.js for projectCode=${projectCodeFromViewer}`
    )

    // Request configurable materials if not already requested
    // Add longer delay to ensure materials are fully loaded
    if (matched) {
      setTimeout(() => {
        iframeRef.current.contentWindow.postMessage(
          {
            action: "getProductNodeInstanceConfigurableMaterials",
            localId: localId,
          },
          "*"
        )
      }, 800) // Increased from 500 to 800ms for better material loading
    }
  }

  /* ----------------------------------------------------------------
     3) SPECIALIZED HANDLERS for collisions, materials, meshSelection...
  -----------------------------------------------------------------*/
  // Configurable materials from viewer
  const handleConfigurableMaterials = (data) => {
    const { localId, configurableMaterials } = data
    console.log(
      `[handleConfigurableMaterials] localId=${localId}, => `,
      configurableMaterials
    )

    // Check if there are default applied materials
    // Each configurableMaterial has an "activeVariation" field that indicates the default material
    const materialsWithDefaults = configurableMaterials.map((material) => {
      // Extract the default material if present
      const appliedVariation =
        material.activeVariation ||
        (material.materialVariations && material.materialVariations.length > 0
          ? material.materialVariations[0].name
          : null)

      // Add the default material as appliedVariation
      return {
        ...material,
        appliedVariation,
      }
    })

    setProductNodes((prev) => {
      const node = prev[localId]
      if (!node) return prev

      return {
        ...prev,
        [localId]: {
          ...node,
          configurableMaterials: materialsWithDefaults,
        },
      }
    })
  }
  // Collisions
  const handleCollisionsUpdate = (data) => {
    const { localIds } = data
    // localIds => the colliding productNodes
    setProductNodes((prev) => {
      const updated = { ...prev }
      Object.keys(updated).forEach((localId) => {
        if (localIds.includes(parseInt(localId))) {
          updated[localId].colliding = true
        } else {
          updated[localId].colliding = false
        }
      })
      return updated
    })

    // update ghost
    const nodesToUpdate = Object.keys(productNodes).map((localId) => ({
      localId: parseInt(localId),
      renderAsGhost: productNodes[localId].colliding,
    }))

    iframeRef.current.contentWindow.postMessage(
      {
        action: "setProductNodesToGhostRenderingMode",
        nodes: nodesToUpdate,
      },
      "*"
    )
  }

  // Mesh selection
  const handleMeshSelection = (data) => {
    const { nodeLocalId, position } = data
    if (productNodes[nodeLocalId]) {
      setProductNodes((prev) => ({
        ...prev,
        [nodeLocalId]: {
          ...prev[nodeLocalId],
          currentHeightFromFloor: position?.[1] || 0,
        },
      }))
      selectProductNodeInstance(productNodes[nodeLocalId])()
    }
  }

  // State handling
  const handleStateChange = (data) => {
    const viewerState = data.state?.viewerState
    if (viewerState === "loaded") {
      console.log(
        "[onStateChange] => viewerState=loaded => fetch getPlannerUsableProjects"
      )

      // Try to load data from cache before making API call
      const loadedFromCache = loadCachedData()

      // Only make API call if we didn't load from cache
      if (!loadedFromCache) {
        iframeRef.current.contentWindow.postMessage(
          {
            action: "getPlannerUsableProjects",
          },
          "*"
        )
      }

      // Try to get and store the Emersya viewer instance
      if (window.emViewers && window.emViewers["emersyaIframe"]) {
        console.log("Emersya viewer detected on state change")
        setEmersyaViewerInstance(window.emViewers["emersyaIframe"])
      }
    }
  }

  /* ----------------------------------------------------------------
     4) UTILITY FUNCTIONS: resize floor, drag, click, removal, rotation...
  -----------------------------------------------------------------*/
  // Resizing floor
  const resizeFloor = () => {
    const { length, width, height } = floorDimensions
    iframeRef.current.contentWindow.postMessage(
      {
        action: "resizePlannerSpace",
        length: parseFloat(length),
        width: parseFloat(width),
        height: parseFloat(height),
      },
      "*"
    )
  }

  // Dragging a product (from left)
  const onDragStart = (productNode) => (event) => {
    setIgnoreClick(true)
    setIsProductDropdownOpen(false)

    const localId = nodeLocalIdsRef.current + 1
    nodeLocalIdsRef.current += 1

    console.log("[onDragStart] About to add localId=", localId, productNode)

    // Set loading state
    setLoadingProducts((prev) => ({
      ...prev,
      [localId]: true,
    }))

    const nodesToAdd = [
      {
        SKU: productNode.SKU,
        projectCode: productNode.projectCode,
        localId,
        parentLocalId: 1,
        position: [0, -0.001, 0],
        rotationAxis: [0, 1, 0],
        rotationAngle: 0,
      },
    ]

    // "startProductNodeDrop" => Emersya will add it with drag
    iframeRef.current.contentWindow.postMessage(
      {
        action: "startProductNodeDrop",
        id: localId,
        nodesToAdd,
      },
      "*"
    )

    setProductNodesToAdd((prev) => ({
      ...prev,
      [localId]: {
        SKU: productNode.SKU,
        projectCode: productNode.projectCode,
        localId,
        parentLocalId: 1,
        refData: {
          ...productNode,
          name: productNode.name,
          previewPath:
            productNode.previewPath ||
            `https://cdn.emersya.com/cl/3IB82Q24UW/p/${productNode.projectCode}/productNodes/86003`,
        },
        position: [0, 0.1, 0],
        rotationAngle: 0,
        configurableMaterials: [],
        height: 1,
      },
    }))

    // Transparent image
    event.dataTransfer.setDragImage(emptyImageRef.current, 0, 0)
    event.preventDefault()
    event.stopPropagation()
  }

  // Click on a product => immediate add
  const onClick = (productNode) => (event) => {
    if (ignoreClick) {
      setIgnoreClick(false)
      return
    }
    event.preventDefault()
    event.stopPropagation()

    const localId = nodeLocalIdsRef.current + 1
    nodeLocalIdsRef.current += 1

    console.log("[onClick] Adding localId=", localId, productNode)

    // Set loading state
    setLoadingProducts((prev) => ({
      ...prev,
      [localId]: true,
    }))

    const nodes = [
      {
        SKU: productNode.SKU,
        projectCode: productNode.projectCode,
        localId,
        parentLocalId: 1,
        draggable: true,
        tracked: true,
        position: [0, 0.1, 0],
        rotationAxis: [0, 1, 0],
        rotationAngle: 0,
        configurableMaterials: [],
      },
    ]

    iframeRef.current.contentWindow.postMessage(
      {
        action: "addProductNodesInstances",
        id: localId,
        nodes,
      },
      "*"
    )

    setProductNodesToAdd((prev) => ({
      ...prev,
      [localId]: {
        SKU: productNode.SKU,
        projectCode: productNode.projectCode,
        localId,
        parentLocalId: 1,
        refData: {
          ...productNode,
          name: productNode.name,
          previewPath:
            productNode.previewPath ||
            `https://cdn.emersya.com/cl/3IB82Q24UW/p/${productNode.projectCode}/productNodes/86003`,
        },
        position: [0, 0, 0],
        rotationAxis: [0, 1, 0],
        rotationAngle: 0,
        height: 1,
      },
    }))
  }

  // Remove product
  const removeProductNodeInstance = (pn) => (event) => {
    event.stopPropagation()
    event.preventDefault()
    console.log("[removeProductNodeInstance] => localId=", pn.localId)
    iframeRef.current.contentWindow.postMessage(
      {
        action: "removeProductNodeInstance",
        localId: pn.localId,
        id: pn.localId,
      },
      "*"
    )
  }

  // Rotate product
  const rotateProductNodeInstance = (pn) => (event) => {
    event.stopPropagation()
    event.preventDefault()
    const angleDeg = parseInt(event.target.value)
    const angleRad = (angleDeg * Math.PI) / 180

    setProductNodes((prev) => ({
      ...prev,
      [pn.localId]: {
        ...prev[pn.localId],
        rotationAngle: angleRad,
      },
    }))

    iframeRef.current.contentWindow.postMessage(
      {
        action: "setProductNodesInstancesTransformations",
        nodes: [
          {
            localId: pn.localId,
            rotationAxis: [0, 1, 0],
            rotationAngle: angleRad,
          },
        ],
      },
      "*"
    )
  }

  // Product height
  const changeProductNodeInstanceHeight = (pn) => (event) => {
    event.stopPropagation()
    event.preventDefault()

    const oldHeight = pn.height || 1
    const newHeight = parseInt(event.target.value) / 100

    // Important: Keep the existing position and only update Y
    const currentPosition = pn.position || [0, 0, 0]
    const newPosition = [
      currentPosition[0],
      currentPosition[1] + (newHeight - oldHeight),
      currentPosition[2],
    ]

    iframeRef.current.contentWindow.postMessage(
      {
        action: "setProductNodesInstancesTransformations",
        nodes: [
          {
            localId: pn.localId,
            position: newPosition,
            height: newHeight,
          },
        ],
      },
      "*"
    )

    // Update state with both new height and position
    setProductNodes((prev) => ({
      ...prev,
      [pn.localId]: {
        ...prev[pn.localId],
        height: newHeight,
        position: newPosition,
      },
    }))
  }

  // Select product
  const selectProductNodeInstance = (pn) => () => {
    iframeRef.current.contentWindow.postMessage(
      {
        action: "selectProductNode",
        localId: pn.localId,
      },
      "*"
    )
  }

  // Apply material => Message to Emersya
  const applyMaterial = (pn, configurableMaterial, materialVariation) => {
    console.log(
      `[applyMaterial] localId=${pn.localId}, configurableMaterial=${configurableMaterial}, variation=${materialVariation}`
    )

    // First update the state to ensure our UI reflects the change immediately
    setProductNodes((prev) => {
      const node = prev[pn.localId]
      if (!node) return prev

      // If the node has configurableMaterials, update the applied variation
      if (node.configurableMaterials) {
        const updatedMaterials = node.configurableMaterials.map((material) => {
          if (material.name === configurableMaterial) {
            return {
              ...material,
              appliedVariation: materialVariation,
              activeVariation: materialVariation,
            }
          }
          return material
        })

        return {
          ...prev,
          [pn.localId]: {
            ...node,
            configurableMaterials: updatedMaterials,
          },
        }
      }

      return prev
    })

    // Then send the message to Emersya to apply the material
    // Add a small delay to ensure the state update happens first
    setTimeout(() => {
      try {
        iframeRef.current.contentWindow.postMessage(
          {
            action: "setMaterials",
            materials: [
              {
                configurableMaterial,
                materialVariation,
                nodeLocalIds: [pn.localId],
              },
            ],
          },
          "*"
        )
      } catch (error) {
        console.error("[applyMaterial] Error:", error)
      }
    }, 50)
  }

  /* ----------------------------------------------------------------
     5) SHIFT => snapping
  -----------------------------------------------------------------*/
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Shift") {
        iframeRef.current?.contentWindow.postMessage(
          { action: "togglePlannerSnapMode", value: true },
          "*"
        )
      }
    }
    const handleKeyUp = (event) => {
      if (event.key === "Shift") {
        iframeRef.current?.contentWindow.postMessage(
          { action: "togglePlannerSnapMode", value: false },
          "*"
        )
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("keyup", handleKeyUp)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("keyup", handleKeyUp)
    }
  }, [])

  /* ----------------------------------------------------------------
     6) Filters and UI => Filter products by "FamilySelector" and dropdown
  -----------------------------------------------------------------*/
  const filteredProducts = useMemo(() => {
    if (!selectedFamily) return productsList
    return productsList.filter((product) => product.family === selectedFamily)
  }, [productsList, selectedFamily])

  const handleFamilyChange = (newFamilyName) => {
    setSelectedFamily(newFamilyName || null)
    setIsFamilyDropdownOpen(false)
    setIsProductDropdownOpen(false)
  }

  const toggleAccordionVisibility = () => {
    setIsAccordionHidden((prevState) => !prevState)
    setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage(
        {
          action: "resizePlannerSpace",
        },
        "*"
      )
    }, 150)
  }

  /**
   * Wrapper function for "applyMaterial", so we pass it to the accordion component with the signature we prefer:
   *   applyMaterial(pn, "imbottito" | "struttura" | etc, "VARIANT NAME")
   */
  function applyMaterialFromAccordion(pn, configurableMat, variationName) {
    applyMaterial(pn, configurableMat, variationName)
  }

  /* ----------------------------------------------------------------
     7) RENDER
  -----------------------------------------------------------------*/
  return (
    <>
      {loading && (
        <div
          className="ay-w-full ay-h-screen ay-bg-white ay-opacity-95 ay-fixed ay-top-0 ay-z-50 ay-flex ay-justify-center ay-items-center ay-text-3xl"
          id="loader"
        >
          <img
            src={logo}
            alt="Logo laCividina"
            className="ay-animate-scale-pulse"
          />
        </div>
      )}

      <div className="ay-flex ay-flex-col md:ay-flex-row ay-h-screen ay-overflow-hidden">
        {/* IFRAME */}
        <div
          className={`ay-relative ${
            isAccordionHidden ? "ay-w-full" : "ay-w-full md:ay-w-4/5"
          } ay-transition-all ay-duration-500 ay-ease-in-out ay-h-screen`}
        >
          <PlannerPlayer iframeRef={iframeRef} onLoad={handleIframeLoad} />

          <button
            onClick={toggleAccordionVisibility}
            className={`ay-absolute ay-transition-transform ay-duration-300 ${
              isAccordionHidden ? "ay-rotate-180" : ""
            } ay-right-4 ay-opacity-20 ay-top-1/2 ay-transform ay--translate-y-1/2 ay-text-3xl ay-font-bold ay-focus:ay-outline-none`}
          >
            <FaChevronRight />
          </button>

          {/* Loading Indicators */}
          {Object.keys(loadingProducts).length > 0 && (
            <div className="ay-absolute ay-top-2 ay-left-1/2 -ay-translate-x-1/2 ay-flex ay-items-center ay-bg-white ay-bg-opacity-80 ay-px-3 ay-py-2 ay-rounded-lg ay-shadow-md">
              <FaSpinner className="ay-animate-spin ay-mr-2" />
              <span className="ay-text-sm">
                Caricamento prodotti... / Loading products...
              </span>
            </div>
          )}
        </div>

        {/* SIDEBAR / UI */}
        <div
          className={`ay-p-5 ay-bg-[#f9f9f9] ay-select-none ay-overflow-auto ${
            isAccordionHidden ? "ay-w-0" : "ay-w-full md:ay-w-3/5 lg:ay-w-2/6"
          } ay-transition-all ay-duration-500 ay-ease-in-out`}
          style={{ display: isAccordionHidden ? "none" : "block" }}
        >
          <div className="ay-flex ay-flex-col ay-h-full">
            {/* Famiglia - Integrated Family Selector */}
            <div className="ay-w-full ay-pt-4 ay-pb-6 ay-border-b ay-border-b-black">
              <div className="ay-group ay-gap-x-4 ay-flex ay-justify-between ay-items-center">
                <h2 className="ay-text-lg ay-font-bold">{labels.family}</h2>
                {selectedFamily && (
                  <p className="ay-uppercase">{selectedFamily}</p>
                )}
              </div>

              <div className="ay-mt-2 ay-relative" ref={familyDropdownRef}>
                <div
                  className="ay-border ay-rounded-lg ay-bg-white ay-border-gray-200 ay-w-full ay-p-4 ay-flex ay-justify-between ay-items-center ay-cursor-pointer"
                  onClick={() => setIsFamilyDropdownOpen(!isFamilyDropdownOpen)}
                >
                  <span>{selectedFamily || labels.allProducts}</span>
                  <span
                    className={`ay-transition-transform ay-duration-300 ${
                      isFamilyDropdownOpen ? "ay-rotate-180" : ""
                    }`}
                  >
                    <FaChevronDown />
                  </span>
                </div>

                {isFamilyDropdownOpen && (
                  <div className="ay-absolute ay-z-10 ay-w-full ay-mt-1 ay-bg-white ay-border ay-border-gray-200 ay-rounded-lg ay-shadow-lg ay-max-h-[70vh] ay-overflow-auto">
                    <div
                      className="ay-p-4 ay-hover:ay-bg-gray-100 ay-border-t-2 ay-border-gray-400 ay-cursor-pointer"
                      onClick={() => handleFamilyChange("")}
                    >
                      {labels.allProducts}
                    </div>

                    {/* Regular families */}
                    {sortedFamilies.map((family, index) => {
                      // Check if this is a special family that needs a divider
                      const isSpecialFamily =
                        family === "PAVIMENTO" || family === "SET"
                      const isFirstSpecialFamily =
                        isSpecialFamily &&
                        sortedFamilies
                          .slice(0, index)
                          .every((f) => f !== "PAVIMENTO" && f !== "SET")

                      return (
                        <div key={family}>
                          {isFirstSpecialFamily && (
                            <div className="ay-border-t-2 ay-border-gray-400 ay-my-2"></div>
                          )}
                          <div
                            className="ay-p-4 ay-hover:ay-bg-gray-100 ay-cursor-pointer"
                            onClick={() => handleFamilyChange(family)}
                          >
                            {getFamilyDisplayName(family)}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Dropdown "Seleziona modello" */}
            <div className="ay-mt-4 ay-group ay-gap-x-4 ay-border-b ay-border-black ay-pb-4">
              <h2 className="ay-text-lg ay-font-bold ay-mb-2">
                {labels.selectModel}
              </h2>
              <div className="ay-relative" ref={productDropdownRef}>
                <div
                  className="ay-border ay-rounded-lg ay-bg-white ay-border-gray-200 ay-w-full ay-p-4 ay-flex ay-justify-between ay-items-center ay-cursor-pointer"
                  onClick={() =>
                    setIsProductDropdownOpen(!isProductDropdownOpen)
                  }
                >
                  <span>
                    {selectedFamily
                      ? `${selectedFamily} (${filteredProducts.length})`
                      : `${labels.allProducts} (${filteredProducts.length})`}
                  </span>
                  <span
                    className={`ay-transition-transform ay-duration-300 ${
                      isProductDropdownOpen ? "ay-rotate-180" : ""
                    }`}
                  >
                    <FaChevronDown />
                  </span>
                </div>

                {isProductDropdownOpen && (
                  <div className="ay-absolute ay-z-10 ay-w-full ay-mt-1 ay-bg-white ay-border ay-border-gray-200 ay-rounded-lg ay-shadow-lg ay-max-h-[60vh] ay-overflow-auto">
                    {filteredProducts.length > 0 ? (
                      filteredProducts.map((product, i) => {
                        // Look for a match in products.js using projectCode
                        const matchedProduct = products.find(
                          (p) => p.projectCode === product.projectCode
                        )

                        // Use the name from the match if available, otherwise the original name
                        const displayName = matchedProduct
                          ? matchedProduct.name
                          : product.name

                        return (
                          <div
                            key={`${product.projectCode}-${product.SKU}-${i}`}
                            className="ay-p-4 ay-hover:bg-gray-100 ay-cursor-pointer ay-flex ay-items-center"
                            draggable
                            onDragStart={onDragStart(product)}
                            onClick={onClick(product)}
                            onPointerDown={() => setIgnoreClick(false)}
                            title={displayName} // Show full name on hover
                          >
                            <img
                              src={product.previewPath || placeholder}
                              alt={displayName}
                              className="ay-w-12 ay-h-12 ay-object-cover ay-mr-4"
                            />
                            <span className="ay-flex-1 ay-truncate">
                              {displayName}
                              {matchedProduct && (
                                <span className="ay-text-xs ay-text-gray-500 ay-block">
                                  {product.projectCode}
                                </span>
                              )}
                            </span>
                          </div>
                        )
                      })
                    ) : (
                      <div className="ay-p-4 ay-text-gray-500">
                        Nessun prodotto disponibile per la famiglia selezionata
                        / No products available for the selected family.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* PlannerActions component - Export/Import functionality */}
            <PlannerActions
              productNodes={productNodes}
              resetScene={resetScene}
              iframeRef={iframeRef}
              setLoadingProducts={setLoadingProducts}
              setProductNodes={setProductNodes}
              setProductNodesToAdd={setProductNodesToAdd}
              nodeLocalIdsRef={nodeLocalIdsRef}
            />

            {/*     <div className="floor-dimensions-inputs">
              <label>
                Length:
                <input
                  type="number"
                  min="1"
                  step="0.1"
                  value={floorDimensions.length}
                  onChange={(e) =>
                    setFloorDimensions((dims) => ({
                      ...dims,
                      length: e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Width:
                <input
                  type="number"
                  min="1"
                  step="0.1"
                  value={floorDimensions.width}
                  onChange={(e) =>
                    setFloorDimensions((dims) => ({
                      ...dims,
                      width: e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Height:
                <input
                  type="number"
                  min="1"
                  step="0.1"
                  value={floorDimensions.height}
                  onChange={(e) =>
                    setFloorDimensions((dims) => ({
                      ...dims,
                      height: e.target.value,
                    }))
                  }
                />
              </label>
              <button onClick={resizeFloor}>Apply Floor Dimensions</button>
            </div> */}

            {/* Products added to scene */}
            <div className="ay-mb-4 ay-mt-4">
              <h2 className="ay-text-lg ay-font-bold ay-mb-4">
                {labels.addedProducts}
              </h2>
              <ProductList
                productNodes={productNodes}
                selectProductNodeInstance={selectProductNodeInstance}
                removeProductNodeInstance={removeProductNodeInstance}
                duplicateProductNode={duplicateProductNode}
                rotateProductNodeInstance={rotateProductNodeInstance}
                changeProductNodeInstanceHeight={
                  changeProductNodeInstanceHeight
                }
                applyMaterial={applyMaterialFromAccordion}
                materials={materiali}
                wood={wood}
                labels={labels}
              />
            </div>

            {/* Logo */}
            <img
              src={logo}
              alt="Logo laCividina"
              className="ay-w-5/6 ay-py-6 ay-pr-5 ay-max-w-52 ay-mt-auto"
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default Planner
